import apiResponse from '@stores/ApiResponse';
import { QuestionView, ResponseView, SectionView } from '@views';
import { emptySection } from '@views/DashboardView';
import { Question } from '@models/Question';
import { Response } from '@models/Response';
import { SurveyView } from '@models/Survey';
import { useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { SurveyStore } from '@app/api/SurveyStore';

export enum SurveyType {
  Onboarding = 'ONBOARDING',
  CohortEnd = 'COHORT_END',
  TeamCelebration = 'TEAM_CELEBRATION',
}

export interface NextSurveySectionResponse {
  section: SectionView;
  surveyType: SurveyType;
  surveyId: number;
}

export class NextSurveySectionViewCreator {
  constructor(private readonly surveyStore: SurveyStore = new SurveyStore()) {}

  async createView(cohortId: number): Promise<NextSurveySectionResponse> {
    const survey = await apiResponse.handleApiResponse<SurveyView>(() =>
      this.surveyStore.GetNextAvailableSurveyForCohort(cohortId)
    );
    const initialSection = survey.sections[0] ?? emptySection;
    const section = {
      ...initialSection,
      description: '',
      questions: initialSection.questions.map(
        (question: Question) =>
          ({
            ...question,
            userResponse: '',
            responses: question.responses.map(
              (response: Response) =>
                ({
                  ...response,
                  selected: false,
                  textResponse: '',
                }) as ResponseView
            ),
          }) as QuestionView
      ),
    } as SectionView;

    return {
      section,
      surveyType: survey.surveyType,
      surveyId: survey.surveyId,
    };
  }
}

export const useNextSurveySectionViewCreator = (groupId: number) =>
  useQuery<NextSurveySectionResponse>({
    queryKey: ['nextSurveySectionViewCreator', groupId],
    queryFn: () => new NextSurveySectionViewCreator().createView(groupId),
    staleTime: Duration.fromObject({ minutes: 1 }).milliseconds,
    enabled: groupId > 0,
  });
